import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Avatar, Box, InputAdornment, Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ArrowRightIcon, SearchIcon } from 'assets/icons';
import {
  CustomTable, PrimarySelect, PrimaryTextField, StickyHeader,
} from 'components';
import { IOption } from 'interfaces';
import { Link, useNavigate } from 'react-router-dom';
import { ExpertHubLayout } from 'components/templates';
import { getAvatarLetters, getUserName } from 'utils/common-utils';
import { selectUsers, selectUsersLoader, selectUsersTotal } from '../selector';
import { IUser } from '../interface';
import { usersAction } from '../slice';

const sortOptions: IOption[] = [
  { id: 1, text: 'First name - AZ', value: 'firstName-asc' },
  { id: 2, text: 'First name - ZA', value: 'firstName-desc' },
  { id: 3, text: 'Last name - AZ', value: 'lastName-asc' },
  { id: 4, text: 'Last name - ZA', value: 'lastName-desc' },
  { id: 5, text: 'Default', value: 'default' },
];

const UsersList = () => {
  const dispatch = useDispatch();
  const [keyword, setKeyword] = React.useState('');
  const [page, setPage] = React.useState(0);
  const navigate = useNavigate();
  const users: IUser[] = useSelector(selectUsers);
  const total = useSelector(selectUsersTotal);
  const loading = useSelector(selectUsersLoader);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  const [selectedSort, setSelectedSort] = React.useState(sortOptions[0].id);

  const itemTableColumns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Name',
      flex: 0.5,
      minWidth: 200,
      renderCell: (params) => (
        <>
          <Avatar
            sx={{ mr: 2 }}
            src={params?.row?.profileImgUrl}
            alt={getAvatarLetters(params?.row)}
          >
            {getAvatarLetters(params?.row)}
          </Avatar>
          <Typography className="lh-1" variant="body2" component="p">
            {getUserName(params?.row)}
          </Typography>
        </>
      ),
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 100,
    },
    {
      field: 'sessionPurchaseCount',
      headerName: 'Sessions Purchased',
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2" component="p" sx={{ textAlign: 'center', width: '100%' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'isFollowing',
      headerName: 'Status',
      minWidth: 150,
      renderCell: (params) => {
        if (params.row.isFollowing && params.row.isSubscribed) {
          return 'Following & Subscribed';
        }
        if (params.row.isFollowing) {
          return 'Following';
        }
        if (params.row.isSubscribed) {
          return 'Subscribed';
        }
        return '-';
      },
    },
    {
      field: 'id',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <Link to={`/users/${params.value}`} className="d-flex align-items-center text-decoration-none">
          <Typography variant="body2" sx={{ color: 'primary.main' }}>
            View user profile
          </Typography>
          <ArrowRightIcon sx={{ fontSize: 20, ml: 1, color: 'primary.main' }} />
        </Link>
      ),
    },
  ];

  React.useEffect(() => {
    dispatch(usersAction.getUsers({
      keyword,
      page: (page + 1),
      limit: 5,
      sortBy: selectedSort as string,
    }));
  }, [dispatch, selectedSort, keyword, page]);

  return (
    <ExpertHubLayout>
      <Box sx={{ mb: 4 }}>
        <StickyHeader>
          <div className="row mb-2">
            <div className=" col-lg-3 col-xl-2 mb-3 mb-md-0 d-flex flex-column justify-content-center">
              <Typography variant="h4" sx={{ mb: 0.5 }}>
                Users
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {!loading && `You have ${total} users.`}
              </Typography>
            </div>
            <div className="col-lg-9 col-xl-10">
              <div className="row justify-content-lg-end">
                <div className="col-sm-6 col-lg-4 col-xl-3 mb-2 mb-md-0">
                  <PrimaryTextField
                    placeholder="Search"
                    endAdornment={(
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    )}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-sm-6 col-lg-4 col-xl-3 mb-2 mb-md-0">
                  <PrimarySelect
                    options={sortOptions}
                    inlineLabel
                    label="Sort"
                    onChange={(e) => {
                      setSelectedSort(e.target.value as string);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </StickyHeader>
      </Box>
      <Box sx={{
        borderRadius: 1.5, backgroundColor: 'background.paper', p: 0,
      }}
      >
        <CustomTable
          rows={users}
          columns={itemTableColumns}
          checkboxSelection={false}
          rowIdProperty="id"
          loading={loading}
          hideFooter={false}
          pagination
          page={page}
          setPage={setPage}
          rowsPerPageOptions={5}
          pageSize={5}
          rowCount={total}
          paginationMode="server"
        />
      </Box>
    </ExpertHubLayout>
  );
};

export default UsersList;
